const HTML_NS = 'http://www.w3.org/1999/xhtml';
const SVG_NS = 'http://www.w3.org/2000/svg';

const HTML_ELEMENTS = new Set([
  'section',
  'h2',
  'h3',
  'p',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'b',
  'i',
  'code',
  'pre',
  'br',
  'hr',
  'a',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'caption',
  'figure',
  'figcaption',
  'details',
  'summary',
  'div',
  'span',
  'blockquote',
  'small',
  'sub',
  'sup'
]);

const HTML_ATTRIBUTES = new Set([
  'class',
  'data-label',
  'data-role',
  'data-chart',
  'data-direction',
  'data-sortable',
  'data-filter-column',
  'colspan',
  'rowspan',
  'scope',
  'open'
]);

const SVG_ELEMENTS = new Set([
  'svg',
  'g',
  'rect',
  'line',
  'path',
  'circle',
  'ellipse',
  'polyline',
  'polygon',
  'text',
  'tspan',
  'title',
  'desc',
  'defs',
  'marker'
]);

// Names are compared case-sensitively: the HTML parser restores SVG's camelCase (viewBox, refX, markerWidth).
const SVG_ATTRIBUTES = new Set([
  'x',
  'y',
  'x1',
  'y1',
  'x2',
  'y2',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'dx',
  'dy',
  'width',
  'height',
  'd',
  'points',
  'transform',
  'viewBox',
  'refX',
  'refY',
  'markerWidth',
  'markerHeight',
  'markerUnits',
  'orient',
  'fill',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'opacity',
  'font-size',
  'font-weight',
  'text-anchor',
  'marker-start',
  'marker-mid',
  'marker-end',
  'role',
  'aria-label'
]);

// Lower-cased, so an SVG name the parser left in the HTML namespace (foreignobject) still matches.
const REMOVED_WITH_CONTENT = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'meta',
  'link',
  'base',
  'foreignobject',
  'use',
  'image',
  'animate',
  'animatemotion',
  'animatetransform',
  'set'
]);

const PAINT_TOKENS = [
  'paper',
  'surface',
  'ink',
  'ink-2',
  'ink-3',
  'rule',
  'brand',
  'brand-2',
  'parent',
  'good',
  'bad',
  'review',
  'on-ink'
];
const PAINT = new RegExp(`^(none|currentColor|var\\(--(${PAINT_TOKENS.join('|')})\\))$`);
const PAINT_ATTRIBUTES = new Set(['fill', 'stroke']);
const MARKER_ATTRIBUTES = ['marker-start', 'marker-mid', 'marker-end'];
const MARKER_ID = /^[A-Za-z][\w-]*$/;
const MARKER_REFERENCE = /^url\(#([A-Za-z][\w-]*)\)$/;

const MAX_PASSES = 3;

/**
 * Keeps only the report vocabulary and the diagram subset of SVG.
 *
 * The result is a fixed point: sanitizing it again returns the same string, so the tree the report
 * template parses from it contains only allowed elements and attributes. One pass is not enough because
 * unwrapping can leave nesting the parser rebuilds differently (an `a` inside an `a` once a MathML wrapper
 * between them is gone). When no fixed point is reached within three passes, only the text is returned.
 */
export function sanitizeReportBody(html: string): string {
  let current = sanitizeOnce(html);
  for (let pass = 1; pass < MAX_PASSES; pass++) {
    const next = sanitizeOnce(current);
    if (next === current) {
      return current;
    }
    current = next;
  }
  const body = parseBody('');
  body.textContent = parseBody(current).textContent;
  return body.innerHTML;
}

function parseBody(html: string): HTMLElement {
  return new DOMParser().parseFromString(html, 'text/html').body;
}

function sanitizeOnce(html: string): string {
  const body = parseBody(html);
  cleanChildren(body, false);
  return body.innerHTML;
}

function cleanChildren(parent: Node, inSvg: boolean): void {
  for (const child of Array.from(parent.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      cleanElement(child as Element, inSvg);
    } else if (child.nodeType !== Node.TEXT_NODE) {
      child.remove();
    }
  }
}

function cleanElement(el: Element, inSvg: boolean): void {
  const name = el.localName;
  const lowerName = name.toLowerCase();
  if (REMOVED_WITH_CONTENT.has(lowerName) || (inSvg && lowerName === 'a')) {
    el.remove();
    return;
  }
  const isSvgElement = el.namespaceURI === SVG_NS;
  const isSvgRoot = !inSvg && isSvgElement && name === 'svg';
  const allowed = inSvg
    ? isSvgElement && SVG_ELEMENTS.has(name)
    : isSvgRoot || (el.namespaceURI === HTML_NS && HTML_ELEMENTS.has(name));
  const childrenInSvg = inSvg || isSvgRoot;
  cleanChildren(el, childrenInSvg);
  if (!allowed) {
    el.replaceWith(...Array.from(el.childNodes));
    return;
  }
  cleanAttributes(el, childrenInSvg);
  // An SVG title or desc parses its content as HTML; text is all a tooltip or description needs.
  if (inSvg && (name === 'title' || name === 'desc')) {
    el.replaceChildren(el.textContent ?? '');
  }
  if (isSvgRoot) {
    // SVG text defaults to black, which disappears on the dark palette.
    if (!el.hasAttribute('fill')) {
      el.setAttribute('fill', 'currentColor');
    }
    dropDanglingMarkerReferences(el);
  }
}

function cleanAttributes(el: Element, svg: boolean): void {
  for (const attr of Array.from(el.attributes)) {
    if (attr.namespaceURI !== null || !keepsAttribute(el, attr.name, attr.value, svg)) {
      el.removeAttributeNode(attr);
    } else if (svg && PAINT_ATTRIBUTES.has(attr.name) && !PAINT.test(attr.value)) {
      attr.value = 'currentColor';
    }
  }
}

function keepsAttribute(el: Element, name: string, value: string, svg: boolean): boolean {
  if (svg) {
    if (name === 'id') {
      return el.localName === 'marker' && MARKER_ID.test(value);
    }
    if (MARKER_ATTRIBUTES.includes(name)) {
      return MARKER_REFERENCE.test(value);
    }
    return SVG_ATTRIBUTES.has(name);
  }
  if (name === 'href') {
    return el.localName === 'a' && value.startsWith('#');
  }
  return HTML_ATTRIBUTES.has(name);
}

function dropDanglingMarkerReferences(svg: Element): void {
  const markerIds = new Set(
    Array.from(svg.getElementsByTagNameNS(SVG_NS, 'marker'), (marker) => marker.id)
  );
  for (const el of [svg, ...Array.from(svg.querySelectorAll('*'))]) {
    for (const name of MARKER_ATTRIBUTES) {
      const id = MARKER_REFERENCE.exec(el.getAttribute(name) ?? '')?.[1];
      if (id !== undefined && !markerIds.has(id)) {
        el.removeAttribute(name);
      }
    }
  }
}
